import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Shared filter state for the Performance pages, backed by the URL query string
 * (shareable links + summary→detail drill-down). Only non-default values are
 * written to the URL to keep it clean. Mirrors the pattern in useLiveTipsState.
 */
export const PERFORMANCE_DEFAULTS = {
  type: "multi",
  period: "weekly",
  track: "all",
  edition: "all",
  fieldSize: "all",
  lens: "auto",
  priceSource: "sp",
  races: "tipped",
  anchor: "",
  from: "",
  to: "",
};

export function usePerformanceFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => ({
      ...PERFORMANCE_DEFAULTS,
      ...Object.fromEntries([...searchParams.entries()]),
    }),
    [searchParams],
  );

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (
          value == null ||
          value === "" ||
          value === PERFORMANCE_DEFAULTS[key]
        ) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /** Build a /performance/detail URL carrying the current filters (+ overrides). */
  const buildDetailUrl = useCallback(
    (overrides = {}) => {
      const merged = { ...filters, ...overrides };
      const params = new URLSearchParams();
      Object.entries(merged).forEach(([key, value]) => {
        if (
          value != null &&
          value !== "" &&
          value !== PERFORMANCE_DEFAULTS[key]
        ) {
          params.set(key, value);
        }
      });
      const qs = params.toString();
      return `/performance/detail${qs ? `?${qs}` : ""}`;
    },
    [filters],
  );

  return { filters, setFilter, buildDetailUrl };
}
